import React, { useEffect, useState, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { Loader2, AlertCircle } from 'lucide-react';
import { predictionsApi } from '../../api/predictions.api';
import { BASIC_COLORS, COLORS } from '../../constants/colors';
import type { BasicHistoryPoint } from '../../types/predictions';

interface BasicHistoryChartProps {
  dotNumber: number;
  basicId: string;
  basicName: string;
  threshold: number;
  weeks?: number;
}

const WEEK_RANGES = [
  { label: '6 months', weeks: 26 },
  { label: '1 year', weeks: 52 },
  { label: '2 years', weeks: 104 },
  { label: '5 years', weeks: 260 },
];

const formatTick = (dateStr: string): string => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};

const CustomTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0].payload as BasicHistoryPoint;
  return (
    <div
      style={{
        background: 'white',
        border: `1px solid ${COLORS.gray[200]}`,
        borderRadius: 8,
        padding: '8px 12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        fontSize: 12,
      }}
    >
      <div style={{ fontWeight: 600, color: COLORS.gray[900], marginBottom: 4 }}>
        {new Date(label).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
      </div>
      <div style={{ color: COLORS.gray[700] }}>
        Percentile: <strong>{point.percentile !== null ? `${point.percentile}th` : 'N/A'}</strong>
      </div>
      <div style={{ color: COLORS.gray[500] }}>
        Measure: {point.measure.toFixed(4)}
      </div>
    </div>
  );
};

/**
 * Weekly historical trend for a single BASIC. Click-to-expand from BasicScoreCard.
 * Chart plots percentile only (0-100) — measure lives in the tooltip since it's a
 * different scale and this never becomes a dual-axis chart.
 */
export const BasicHistoryChart: React.FC<BasicHistoryChartProps> = ({
  dotNumber,
  basicId,
  basicName,
  threshold,
  weeks: initialWeeks = 104,
}) => {
  const [weeks, setWeeks] = useState(initialWeeks);
  const [points, setPoints] = useState<BasicHistoryPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    predictionsApi
      .getBasicWeeklyHistory(dotNumber, basicId, weeks)
      .then((result) => {
        if (cancelled) return;
        setPoints(result.points);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load history');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dotNumber, basicId, weeks]);

  const lineColor = BASIC_COLORS[basicId] || COLORS.primary.DEFAULT;

  const chartData = useMemo(
    () => points.map((p) => ({ ...p, percentileValue: p.percentile ?? null })),
    [points]
  );

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mt-2">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h4 className="text-sm font-bold text-gray-900">{basicName} — Weekly Trend</h4>
          <p className="text-xs text-gray-500">Percentile ranking over time (lower is better)</p>
        </div>
        <div className="flex gap-1">
          {WEEK_RANGES.map((r) => (
            <button
              key={r.weeks}
              onClick={() => setWeeks(r.weeks)}
              className="px-2.5 py-1 text-xs rounded border transition-colors"
              style={
                weeks === r.weeks
                  ? { background: COLORS.primary.DEFAULT, color: 'white', borderColor: COLORS.primary.DEFAULT }
                  : { background: 'white', color: COLORS.gray[600], borderColor: COLORS.gray[300] }
              }
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16 text-gray-400 gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Calculating {weeks} weeks of history…</span>
        </div>
      )}

      {!loading && error && (
        <div className="flex items-center justify-center py-16 text-red-500 gap-2">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {!loading && !error && chartData.length === 0 && (
        <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
          No history available for this BASIC yet.
        </div>
      )}

      {!loading && !error && chartData.length > 0 && (
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gray[100]} vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={formatTick}
              tick={{ fontSize: 11, fill: COLORS.gray[500] }}
              stroke={COLORS.gray[300]}
              minTickGap={40}
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fontSize: 11, fill: COLORS.gray[500] }}
              stroke={COLORS.gray[300]}
              width={32}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine
              y={threshold}
              stroke={COLORS.danger.DEFAULT}
              strokeDasharray="4 4"
              strokeWidth={1.5}
              label={{ value: `Threshold ${threshold}th`, position: 'insideTopRight', fontSize: 10, fill: COLORS.danger.DEFAULT }}
            />
            <Line
              type="monotone"
              dataKey="percentileValue"
              stroke={lineColor}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 5 }}
              connectNulls
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default BasicHistoryChart;
